// Turns the character count of a help center into the band, score and advice
// the AI agent settings card shows. The count itself comes from the server.

// Below this the agent almost never has enough of a page to answer from it.
export const THIN_ARTICLE_CHARS = 500

// Four equal slices of the track, so every band label stays readable however
// far apart their ceilings are.
export const COVERAGE_BANDS = [
	{ label: "Starting out", from: 0, to: 10_000 },
	{ label: "Basics", from: 10_000, to: 50_000 },
	{ label: "Broad", from: 50_000, to: 150_000 },
	{ label: "Deep", from: 150_000, to: 400_000 },
] as const

const TIERS = [
	{ label: "Empty", min: 0 },
	{ label: "Starting out", min: 1 },
	{ label: "Basics covered", min: 10_000 },
	{ label: "Broad", min: 50_000 },
	{ label: "Deep", min: 150_000 },
] as const

export interface CoverageInput {
	chars: number
	thin: number
	groups: { name: string | null; chars: number }[]
}

// Whole bands cleared, plus how far into the current one the count sits.
export function coverageScore(chars: number): number {
	let filled = 0
	for (const band of COVERAGE_BANDS) {
		if (chars >= band.to) {
			filled += 1
			continue
		}
		if (chars > band.from) filled += (chars - band.from) / (band.to - band.from)
		break
	}
	return Math.round((filled / COVERAGE_BANDS.length) * 100)
}

export function coverageTierIndex(chars: number): number {
	return TIERS.reduce(
		(found, tier, index) => (chars >= tier.min ? index : found),
		0,
	)
}

export const coverageTierLabel = (chars: number): string =>
	TIERS[coverageTierIndex(chars)]!.label

export const nextCoverageTier = (
	chars: number,
): { label: string; at: number } | null => {
	const next = TIERS[coverageTierIndex(chars) + 1]
	return next ? { label: next.label, at: next.min } : null
}

export interface CoverageAdvice {
	tip: string
	action: string
	to: string
}

const DOCS = "/edit-docs"
const GITHUB = "/settings/integrations/github"

export function coverageAdvice(coverage: CoverageInput): CoverageAdvice {
	const { chars, thin, groups } = coverage
	const tier = coverageTierIndex(chars)

	// Under the first band a workspace has barely any articles, so the codebase
	// map is worth more than any single page they could write next.
	if (chars === 0) {
		return {
			tip: "Your help center is empty, so every question reaches your team. Productlane reads your repository once and drafts agent only articles that map your codebase: every function, every button on every page.",
			action: "Connect GitHub",
			to: GITHUB,
		}
	}

	if (tier === 1) {
		return {
			tip: "Enough for a question or two. Productlane reads your repository once and drafts agent only articles for the rest of your product, then keeps them current as you ship.",
			action: "Connect GitHub",
			to: GITHUB,
		}
	}

	if (tier === 2) {
		const thinnest = [...groups]
			.filter((group): group is { name: string; chars: number } => !!group.name)
			.sort((a, b) => a.chars - b.chars)[0]
		return {
			tip: thinnest
				? `Enough for setup questions. ${thinnest.name} holds ${thinnest.chars.toLocaleString("en-US")} characters, so those threads still land on your team.`
				: "Enough for setup questions. Group your articles by topic to see which areas are thin.",
			action: "Open your help center",
			to: DOCS,
		}
	}

	if (tier === 3) {
		return {
			tip: thin
				? `Most questions have a page behind them. ${thin} ${thin === 1 ? "article is" : "articles are"} under ${THIN_ARTICLE_CHARS} characters, and the agent rarely answers from those.`
				: "Most questions have a page behind them. Depth per article now beats new pages.",
			action: "Open your help center",
			to: DOCS,
		}
	}

	return {
		tip: "Plenty for the agent to work with. Keep it current by accepting the docs proposals that closed threads produce.",
		action: "Review the docs proposals",
		to: DOCS,
	}
}
